import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import {
  Button,
  Checkbox,
  EmptyState,
  Icons,
  SearchInput,
  Typography,
  useAlertContext,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  DRAG_HANDLE_RESERVE,
  ITEM_HEADER_ROW_HEIGHT,
  PAR_COLUMN_WIDTH,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { EditOrderGuideActionType } from '@/experiences/sales-portal/domain/order-guides/blocs/reducers/use-edit-order-guide-reducer';
import { mapFilteredReorder } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/transformers/map-filtered-reorder';
import { OrderGuideEditability } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/editability-utils';
import { OrderGuideGroupSelector } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-group-selector';
import { OrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { OrderGuideItemRow } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-item-row';
import { OrderGuideSelectionToolbar } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-selection-toolbar';
import { OrderGuideViewOnlyBanner } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-view-only-banner';
import { SortableList } from '@/experiences/sales-portal/components/sortable-list';
import { useCallback, useDeferredValue, useMemo, useRef } from 'react';
import {
  useCurrentOrderGuideEditability,
  useDeleteItems,
  useEditOrderGuideActions,
  useEditOrderGuideState,
  usePartitionSelection,
  useSelectedItems,
  useSelectItem,
  useSetItemParLevel,
  useSetPartitionSelection,
  useValidatePartitionName,
} from '@/experiences/sales-portal/domain/order-guides/blocs/contexts/edit-order-guide-context';
import { useExpandedItemGroups } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/hooks/use-expanded-item-groups';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useFilteredItemGroups } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/hooks/use-filtered-item-groups';
import { v4 as uuidv4 } from 'uuid';
import { View } from 'react-native';

const BANNER_COPY: Record<
  Exclude<OrderGuideEditability, 'editable'>,
  { descriptionKey: string; titleKey: string }
> = {
  allGuidesLocked: {
    descriptionKey:
      'sales_portal.order_guides_list.view_only.all_guides_locked.description',
    titleKey:
      'sales_portal.order_guides_list.view_only.all_guides_locked.title',
  },
  defaultGuideLocked: {
    descriptionKey:
      'sales_portal.order_guides_list.view_only.this_guide_locked.description',
    titleKey:
      'sales_portal.order_guides_list.view_only.this_guide_locked.title',
  },
};

const PLUS_ICON_SIZE = 14;

interface OrderGuideItemsContentProps {
  onAddItems: () => void;
}

export const OrderGuideItemsContent: React.FC<OrderGuideItemsContentProps> = ({
  onAddItems,
}) => {
  const [styles, theme] = useStyles();
  const { partitions } = useEditOrderGuideState();
  const { selectPartition, selectedPartition } = usePartitionSelection();
  const editability = useCurrentOrderGuideEditability();
  const dispatch = useEditOrderGuideActions();
  const { alert } = useAlertContext('order_guide_delete_items');
  const { showToast } = useExperienceToasts();
  const isEditable = editability === 'editable';
  const { isItemExpanded, toggleItemExpanded } = useExpandedItemGroups();
  const setItemParLevel = useSetItemParLevel();
  const validateGroupName = useValidatePartitionName();
  const addItemLabel = i18n.t(
    'sales_portal.order_guides_list.add_items.list_row_label',
  );

  // Reserve the handle column width so the Par header stays above the par column.
  const dragHandleReserve = isEditable ? DRAG_HANDLE_RESERVE : 0;

  // Defer the group switch (not its contents) so the pill highlight flips
  // instantly; resolving from live `partitions` keeps reorders showing on drop.
  const renderedPartitionUUID = useDeferredValue(
    selectedPartition?.partitionUUID ?? null,
  );
  const renderedPartition =
    partitions.find(
      (partition) => partition.partitionUUID === renderedPartitionUUID,
    ) ?? null;
  const itemGroups = useMemo(
    () => renderedPartition?.itemGroups ?? [],
    [renderedPartition],
  );

  const { deferredSearchTerm, filteredItemGroups, searchTerm, setSearchTerm } =
    useFilteredItemGroups(itemGroups);

  const selectedGroupName =
    renderedPartition?.name ??
    i18n.t('sales_portal.order_guides_list.groups.default');

  const selectedItems = useSelectedItems();
  const selectItem = useSelectItem();
  const setPartitionSelection = useSetPartitionSelection();
  const deleteItems = useDeleteItems();

  const allPartitionPacks = useMemo(
    () => itemGroups.flatMap((group) => group.items),
    [itemGroups],
  );

  const visiblePacks = useMemo(
    () => filteredItemGroups.flatMap((group) => group.items),
    [filteredItemGroups],
  );

  const selectedPackUUIDs = useMemo(
    () =>
      new Set(
        selectedItems
          .filter(
            (selected) => selected.partitionUUID === renderedPartitionUUID,
          )
          .map((selected) => selected.variantPackUUID),
      ),
    [selectedItems, renderedPartitionUUID],
  );

  // Read the latest selection from a ref while not listing selectedPackUUIDs
  // in its deps to prevent rebuilds on every toggle
  const selectedPackUUIDsRef = useRef(selectedPackUUIDs);
  selectedPackUUIDsRef.current = selectedPackUUIDs;

  // Toolbar count is by item: a multi-pack item with several variants
  // selected still counts once.
  const selectedItemCount = useMemo(
    () =>
      new Set(
        selectedItems.map(
          (selected) => `${selected.partitionUUID}:${selected.itemUUID}`,
        ),
      ).size,
    [selectedItems],
  );

  const allSelected =
    visiblePacks.length > 0 &&
    visiblePacks.every((pack) => selectedPackUUIDs.has(pack.variantPackUUID));
  const someSelected = visiblePacks.some((pack) =>
    selectedPackUUIDs.has(pack.variantPackUUID),
  );

  const handleToggleSelectPack = useCallback(
    (variantPackUUID: string, itemUUID: string) => {
      if (renderedPartition) {
        selectItem({
          itemUUID,
          partitionUUID: renderedPartition.partitionUUID,
          variantPackUUID,
        });
      }
    },
    [renderedPartition, selectItem],
  );

  const handleToggleSelectGroup = useCallback(
    (groupPacks: OrderGuideItem[]) => {
      if (!renderedPartition) {
        return;
      }
      const selected = selectedPackUUIDsRef.current;
      const groupUUIDs = new Set(
        groupPacks.map((pack) => pack.variantPackUUID),
      );
      const groupAllSelected = groupPacks.every((pack) =>
        selected.has(pack.variantPackUUID),
      );
      const others = allPartitionPacks.filter(
        (pack) =>
          !groupUUIDs.has(pack.variantPackUUID) &&
          selected.has(pack.variantPackUUID),
      );
      const next = groupAllSelected ? others : [...others, ...groupPacks];
      setPartitionSelection(
        renderedPartition.partitionUUID,
        next.map((pack) => ({
          itemUUID: pack.itemUUID,
          variantPackUUID: pack.variantPackUUID,
        })),
      );
    },
    [renderedPartition, allPartitionPacks, setPartitionSelection],
  );

  const handleToggleSelectAll = useCallback(() => {
    if (!renderedPartition) {
      return;
    }
    const selected = selectedPackUUIDsRef.current;
    const visibleUUIDs = new Set(
      visiblePacks.map((pack) => pack.variantPackUUID),
    );
    const visibleAllSelected =
      visiblePacks.length > 0 &&
      visiblePacks.every((pack) => selected.has(pack.variantPackUUID));
    // Selections outside the visible set are preserved across the toggle.
    const others = allPartitionPacks.filter(
      (pack) =>
        !visibleUUIDs.has(pack.variantPackUUID) &&
        selected.has(pack.variantPackUUID),
    );
    const next = visibleAllSelected ? others : [...others, ...visiblePacks];
    setPartitionSelection(
      renderedPartition.partitionUUID,
      next.map((pack) => ({
        itemUUID: pack.itemUUID,
        variantPackUUID: pack.variantPackUUID,
      })),
    );
  }, [
    renderedPartition,
    visiblePacks,
    allPartitionPacks,
    setPartitionSelection,
  ]);

  const handleDeleteSelected = useCallback(() => {
    const isSingle = selectedItemCount === 1;
    alert({
      buttons: [
        {
          action: () => {},
          style: 'cancel',
          title: i18n.t('cancel'),
        },
        {
          action: () => {
            deleteItems(selectedItems);
            showToast({
              title: i18n.t(
                isSingle
                  ? 'sales_portal.order_guides_list.delete_confirm.success'
                  : 'sales_portal.order_guides_list.delete_confirm.success_plural',
                { count: selectedItemCount },
              ),
              variant: 'success',
            });
          },
          style: 'destructive',
          title: i18n.t('sales_portal.order_guides_list.selection.delete'),
        },
      ],
      message: i18n.t(
        isSingle
          ? 'sales_portal.order_guides_list.delete_confirm.message'
          : 'sales_portal.order_guides_list.delete_confirm.message_plural',
      ),
      title: i18n.t(
        isSingle
          ? 'sales_portal.order_guides_list.delete_confirm.title'
          : 'sales_portal.order_guides_list.delete_confirm.title_plural',
        { count: selectedItemCount },
      ),
    });
  }, [alert, deleteItems, selectedItemCount, selectedItems, showToast]);

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (!renderedPartition) {
      return;
    }
    const mapped = mapFilteredReorder(
      itemGroups,
      filteredItemGroups,
      fromIndex,
      toIndex,
    );
    dispatch({
      payload: {
        fromIndex: mapped.fromIndex,
        partitionUUID: renderedPartition.partitionUUID,
        toIndex: mapped.toIndex,
      },
      type: EditOrderGuideActionType.REORDER_ITEMS,
    });
  };

  const handleCommitParLevel = useCallback(
    (variantPackUUID: string, parLevel: number | null) => {
      if (renderedPartition) {
        setItemParLevel(
          renderedPartition.partitionUUID,
          variantPackUUID,
          parLevel,
        );
      }
    },
    [renderedPartition, setItemParLevel],
  );

  const handleCreateGroup = useCallback(
    (name: string) => {
      const partitionUUID = uuidv4();
      dispatch({
        payload: { name, partitionUUID },
        type: EditOrderGuideActionType.CREATE_PARTITION,
      });
      selectPartition(partitionUUID);
    },
    [dispatch, selectPartition],
  );

  return (
    <View style={styles.content}>
      <View>
        {isEditable ? null : (
          <OrderGuideViewOnlyBanner
            description={i18n.t(BANNER_COPY[editability].descriptionKey)}
            title={i18n.t(BANNER_COPY[editability].titleKey)}
          />
        )}
        <View style={styles.searchBar}>
          <SearchInput
            aria-label={i18n.t(
              'sales_portal.order_guides_list.items.search_aria_label',
            )}
            clearAriaLabel={i18n.t(
              'sales_portal.order_guides_list.items.search_clear',
            )}
            onChangeText={setSearchTerm}
            placeholder={i18n.t(
              'sales_portal.order_guides_list.items.search_placeholder',
              { groupName: selectedGroupName },
            )}
            value={searchTerm}
          />
        </View>
        <OrderGuideGroupSelector
          onCreateGroup={isEditable ? handleCreateGroup : undefined}
          onSelectPartition={selectPartition}
          partitions={partitions}
          selectedPartitionUUID={selectedPartition?.partitionUUID ?? null}
          validateGroupName={validateGroupName}
        />
      </View>
      <View style={styles.itemsSection}>
        <XStack
          alignItems="center"
          justifyContent="space-between"
          paddingRight={theme.sk.spacing.spaceSmall + dragHandleReserve}
          paddingVertical="$10"
          style={styles.header}
        >
          <XStack alignItems="center" gap={theme.sk.spacing.spaceSMedium}>
            {isEditable && visiblePacks.length > 0 ? (
              <Checkbox
                accessibilityLabel={i18n.t(
                  'sales_portal.order_guides_list.selection.select_all',
                )}
                checked={
                  allSelected ? true : someSelected ? 'indeterminate' : false
                }
                marginLeft={theme.sk.spacing.spaceSmall}
                onCheckedChange={handleToggleSelectAll}
                size="$sm"
              />
            ) : null}
            <Typography
              color={theme.sk.color.text.secondary}
              marginLeft={theme.sk.spacing.spaceXsmall}
              size="$xs"
              variant="label"
            >
              {i18n.t('sales_portal.order_guides_list.items.header_item')}
            </Typography>
          </XStack>
          <View style={styles.parHeader}>
            <Typography
              color={theme.sk.color.text.secondary}
              size="$xs"
              variant="label"
            >
              {i18n.t('sales_portal.order_guides_list.items.header_par')}
            </Typography>
          </View>
        </XStack>
        {!renderedPartition || itemGroups.length === 0 ? (
          <View style={styles.errorState}>
            <EmptyState
              action={
                isEditable ? (
                  <Button
                    onPress={onAddItems}
                    size="$md"
                    style={styles.addItemButton}
                    variant="secondary"
                  >
                    {addItemLabel}
                  </Button>
                ) : null
              }
              decoration={
                <EmptyDottedIcon stroke={theme.sk.color.icon.primary} />
              }
              description={i18n.t(
                isEditable
                  ? 'sales_portal.order_guides_list.items.empty_partition_description'
                  : 'sales_portal.order_guides_list.items.empty_partition_description_view_only',
              )}
              title={i18n.t(
                'sales_portal.order_guides_list.items.empty_partition_title',
              )}
            />
          </View>
        ) : filteredItemGroups.length === 0 ? (
          <View style={styles.errorState}>
            <EmptyState
              decoration={
                <EmptyDottedIcon stroke={theme.sk.color.icon.primary} />
              }
              description={i18n.t(
                'sales_portal.order_guides_list.items.no_results_description',
              )}
              title={i18n.t(
                'sales_portal.order_guides_list.items.no_results_title',
                { term: deferredSearchTerm.trim() },
              )}
            />
          </View>
        ) : (
          <SortableList
            disabled={!isEditable}
            dragHandleAccessibilityLabel={i18n.t(
              'sales_portal.order_guides_list.items.reorder_handle',
            )}
            dragHandleAnchorHeight={ITEM_HEADER_ROW_HEIGHT}
            estimateItemHeight={ITEM_HEADER_ROW_HEIGHT}
            footer={
              isEditable ? (
                <Button
                  icon={
                    <Icons.Plus
                      color={theme.sk.color.icon.secondary}
                      size={PLUS_ICON_SIZE}
                    />
                  }
                  onPress={onAddItems}
                  size="$sm"
                  style={styles.addItemButton}
                  variant="tertiary"
                >
                  {addItemLabel}
                </Button>
              ) : undefined
            }
            getItemId={(group) => group.itemUUID}
            items={filteredItemGroups}
            onReorder={handleReorder}
            renderItemContent={(group) => (
              <OrderGuideItemRow
                isEditable={isEditable}
                isExpanded={isItemExpanded(group.itemUUID)}
                onCommitParLevel={handleCommitParLevel}
                onToggleExpanded={toggleItemExpanded}
                onToggleSelectGroup={
                  isEditable ? handleToggleSelectGroup : undefined
                }
                onToggleSelectPack={
                  isEditable ? handleToggleSelectPack : undefined
                }
                packs={group.items}
                selectedPackUUIDs={selectedPackUUIDs}
              />
            )}
            rowStyle={styles.rowBorder}
            showHandleOnHover
          />
        )}
      </View>
      <OrderGuideSelectionToolbar
        onDelete={handleDeleteSelected}
        selectedCount={selectedItemCount}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  addItemButton: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
  content: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    minHeight: 0,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  errorState: {
    flex: 1,
    height: '100%',
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  header: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
  },
  itemsSection: {
    flex: 1,
    minHeight: 0,
  },
  parHeader: {
    alignItems: 'center',
    width: PAR_COLUMN_WIDTH,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
  },
  searchBar: {
    marginBottom: theme.sk.spacing.spaceSMedium,
  },
}));
